/**
 * Morph 솔루션 상세 페이지 — 판독보조 (MR/CT)
 *
 * PO 원칙 준수:
 * - 원칙1: 정상/이상 자동 판정 없음. Z-score·부피% 수치 표시만. 임상 해석은 담당의 영역.
 * - 원칙5: Z-score · 영역별 부피% 표준 단위 명시. 동년배 정상군 대비.
 * - 원칙6: "외부 영상을 재분석하지 않습니다". 외부 결과는 수치 기록 + 단순 비교만.
 *           직접 비교는 임상 해석 필요.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';

const S: React.CSSProperties = { padding: '100px 0', borderTop: '1px solid var(--line)' };

function Page() {
  return (
    <PublicLayout>
      <SolutionLayout
        stage="img"
        badgeLabel="IMG · 판독보조"
        name="Morph"
        tagline="MR과 CT 영상에서 뇌 영역별 부피와 위축 정도를 동년배 정상군 대비 Z-score 등 표준 단위로 산출합니다. ATN의 N(Neurodegeneration)을 객관적으로 평가합니다."
        domain="MR · CT · Z-score · 영역별 부피% · ATN-N"
        crossSell={[
          {
            href: '/solutions/amylo-cloud',
            name: 'Amylo-Cloud',
            desc: 'ATN의 A — Amyloid PET Centiloid 정량',
            stage: 'img',
          },
          {
            href: '/solutions/scst',
            name: 'SCST',
            desc: '신의료기술 통과 · 5개 인지 영역 표준화 점수',
            stage: 'dx',
          },
          {
            href: '/solutions/pascal-med',
            name: 'PASCAL-Med',
            desc: '의료기관용 디지털 인지 중재 솔루션',
            stage: 'int',
          },
        ]}
        disclaimerItems={[
          {
            label: 'Morph 사용 목적',
            text: 'Morph는 MR/CT 영상의 뇌 위축 정량 분석 판독보조 도구입니다. Z-score·영역별 부피% 수치의 임상적 해석(정상/이상 판정, 치료 방향 결정 등)은 담당의 종합 판단에 따릅니다.',
          },
          {
            label: '외부 영상 재분석 금지',
            text: 'BeauBrain은 외부 영상을 재분석하지 않습니다. 외부 기관 분석 결과와의 단순 수치 비교 시 분석 장비·프로토콜이 다를 수 있으므로 임상적 해석이 필요합니다.',
          },
          {
            label: '책임의 분리',
            text: '진료 행위 및 치료 결과에 대한 최종 책임은 담당의 및 해당 의료기관에 있습니다. BeauBrain은 의료기관에 도구를 공급하는 의료기기·서비스 사업자입니다.',
          },
        ]}
      >

        {/* ── 핵심 지표 + 모달리티 설명 ────────────────────────── */}
        <section style={S}>
          <div className="container">
            {/* Z-score 개념 설명 박스 */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(167,139,250,0.1) 0%, rgba(139,92,246,0.06) 100%)',
                border: '1px solid rgba(167,139,250,0.3)',
                borderRadius: 20,
                padding: '32px 36px',
                marginBottom: 48,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                표준 단위 Z-score · 영역별 부피%
              </div>
              <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                Z-score는 동년배 정상군 대비 표준편차로, 장비·프로토콜이 달라도 기관 간·시기 간 비교가 용이한 표준 단위입니다.
                영역별 부피%와 함께 종단 수치 비교 및 외부 의료기관 분석 결과와의 단순 수치 비교에 적합합니다.
              </p>
            </div>

            <SectionBlock label="Key Metrics" title="한눈에 보는 Morph">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 40 }}>
                {[
                  { num: 'MR/CT', label: '2가지 모달리티' },
                  { num: 'Z', label: 'Z-score 표준 단위' },
                  { num: '%', label: '영역별 부피%' },
                  { num: '종단', label: '수치 비교 기록' },
                ].map(({ num, label }) => (
                  <div key={label} className="b-item" style={{ textAlign: 'center', padding: '32px 20px' }}>
                    <div className="num" style={{ fontSize: 40, fontWeight: 800, color: '#a78bfa', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 10 }}>
                      {num}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── MR vs CT 두 모달리티 ─────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock
              label="Two Modalities"
              title="MR과 CT, 의료기관 환경에 맞게 선택"
              subtitle="의료기관에서 보유하거나 환자가 외부에서 받아온 영상의 종류에 따라 MR 또는 CT 분석을 선택할 수 있습니다."
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 56 }}>
                <div className="b-item" style={{ padding: '40px' }}>
                  <span
                    style={{
                      display: 'inline-block', padding: '5px 12px', borderRadius: 4,
                      background: '#a78bfa', color: 'white',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', marginBottom: 20,
                    }}
                  >
                    Morph (MR)
                  </span>
                  <div className="b-title">MR 영상 기반 정밀 분석</div>
                  <p className="b-desc" style={{ marginBottom: 20 }}>
                    고해상도 MR 영상에서 뇌 영역별 부피와 위축 정도를 정밀하게 분석합니다.
                    알츠하이머 의심 환자의 정밀 평가에 적합합니다.
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {['해마·내측측두엽 부피 측정', '피질 두께 분석', '동년배 정상군 대비 위축률 (Z-score)', '고해상도 영상 정량화'].map((item) => (
                      <li key={item} style={{ fontSize: 13, color: 'var(--ink-2)', paddingLeft: 18, position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0, color: '#34d399', fontWeight: 700 }}>✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="b-item" style={{ padding: '40px' }}>
                  <span
                    style={{
                      display: 'inline-block', padding: '5px 12px', borderRadius: 4,
                      background: 'var(--accent)', color: '#000',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', marginBottom: 20,
                    }}
                  >
                    Morph (CT)
                  </span>
                  <div className="b-title">CT 영상 기반 빠른 평가</div>
                  <p className="b-desc" style={{ marginBottom: 20 }}>
                    접근성이 높은 CT 영상으로도 뇌 위축을 평가할 수 있습니다.
                    MR이 어려운 환자나 1차 의료기관·중소병원 환경에 적합합니다.
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {['전체 뇌 부피 측정', '주요 영역 위축 평가', '빠른 영상 분석', '접근성 높은 영상 활용'].map((item) => (
                      <li key={item} style={{ fontSize: 13, color: 'var(--ink-2)', paddingLeft: 18, position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0, color: '#34d399', fontWeight: 700 }}>✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 3가지 임상 가치 ───────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock label="Why Morph" title="뇌 위축 평가의 객관성">
              <div className="apple-bento" style={{ marginTop: 56 }}>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 01</span>
                  <div className="b-title">표준 단위로 객관화</div>
                  <p className="b-desc">
                    육안 판단의 주관성을 보완합니다. 뇌 위축 정도를 동년배 정상군 대비
                    Z-score, 영역별 부피% 등 표준 단위로 산출하여 의사의 판단을 객관적으로 지원합니다.
                    <strong> 정상/이상 판정 등 임상 해석은 담당의 영역입니다.</strong>
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 02</span>
                  <div className="b-title">MR/CT 모두 지원</div>
                  <p className="b-desc">
                    의료기관 환경에 따라 MR과 CT 중 가용한 영상을 사용할 수 있습니다.
                    환자가 외부에서 받아온 영상도 업로드 가능합니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 03</span>
                  <div className="b-title">종단 수치 추적 + ATN 완성</div>
                  <p className="b-desc">
                    동일 환자의 시기별 분석 결과를 차트에서 시계열 비교 가능합니다.
                    외부 의료기관 분석 결과 수치도 차트에 함께 기록하여 단순 비교할 수 있습니다.
                    Amylo(A)·SCST와 함께 ATN의 N(Neurodegeneration)을 완성합니다.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 + 외부 영상 정책 ──────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 56 }}>
                {[
                  {
                    title: '자동 영역 분할',
                    desc: '뇌 영역(해마·측두엽·전두엽 등)을 자동으로 분할하여 영역별 부피를 측정합니다.',
                  },
                  {
                    title: 'Z-score · 부피% 표준 단위 산출',
                    desc: '동년배 정상군 대비 Z-score, 영역별 부피% 등 표준 단위로 산출하여 장비·기관 간 비교가 용이합니다.',
                  },
                  {
                    title: '정량 분석 리포트',
                    desc: '컬러맵과 그래프로 시각화된 정량 분석 리포트를 제공합니다. 정상/이상 판정 등 임상 해석은 담당의 판단 영역입니다.',
                  },
                  {
                    title: 'DICOM 표준 지원',
                    desc: '의료 영상 표준 DICOM 포맷을 지원합니다. 일반 MR/CT 장비 영상과 호환됩니다.',
                  },
                  {
                    title: '종단 수치 비교',
                    desc: '동일 환자의 시기별 분석 결과를 시계열로 비교 가능합니다. 외부 의료기관 분석 결과 수치도 차트에 함께 기록하여 단순 비교할 수 있습니다.',
                  },
                  {
                    title: 'ATN 통합 리포트',
                    desc: 'Amylo·SCST 결과와 통합되어 ATN 풀스택 종합 리포트 자동 생성이 가능합니다.',
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="b-item" style={{ display: 'flex', gap: 16, padding: '28px 24px' }}>
                    <div
                      style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: 'rgba(167,139,250,0.12)', color: '#a78bfa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 700,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 원칙6 외부 영상 정책 박스 */}
              <div
                style={{
                  marginTop: 32,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 16,
                  padding: '28px 32px',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 16 }}>
                  외부 분석 결과 활용 정책
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { ok: true,  text: '외부 의료기관 분석 결과 수치를 차트에 기록하여 단순 비교할 수 있습니다.' },
                    { ok: true,  text: 'BeauBrain은 외부 영상을 재분석하지 않습니다.' },
                    { ok: true,  text: '분석 장비·프로토콜이 다를 수 있으므로, 직접 비교에는 임상적 해석이 필요합니다.' },
                    { ok: false, text: '외부 MR/CT 영상을 Morph로 재분석하는 것은 허가 사용 목적 범위 밖입니다.' },
                  ].map(({ ok, text }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                      <span style={{ color: ok ? '#34d399' : '#f87171', fontWeight: 700, flexShrink: 0 }}>{ok ? '✓' : '✗'}</span>
                      {text}
                    </div>
                  ))}
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 760, margin: '56px auto 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  {
                    q: '왜 Z-score 등 표준 단위를 사용하나요?',
                    a: 'Z-score는 동년배 정상군 대비 표준편차로, 장비·프로토콜이 달라도 기관 간·시기 간 비교가 용이한 표준 단위입니다. 영역별 부피%와 함께 종단 수치 비교 및 외부 의료기관 분석 결과와의 단순 비교에 적합합니다.',
                  },
                  {
                    q: 'MR과 CT 중 어떤 것을 사용해야 하나요?',
                    a: '정밀 평가가 필요하면 MR을, 접근성과 속도가 중요하면 CT를 활용할 수 있습니다. 의료기관의 영상 환경과 환자 상태에 따라 선택하시면 됩니다.',
                  },
                  {
                    q: '의료기관에 MR/CT 장비가 없는데 사용 가능한가요?',
                    a: '네. 환자가 외부 영상의학과에서 받아온 영상을 업로드하여 분석할 수 있습니다. 영상 장비가 없는 의료기관도 Z-score 정량 평가 결과를 환자에게 설명할 수 있습니다.',
                  },
                  {
                    q: '외부 의료기관 분석 결과와 비교할 수 있나요?',
                    a: '외부 기관의 분석 결과 수치를 환자 차트에 함께 기록하여 단순 수치 비교가 가능합니다. 단, BeauBrain은 외부 영상을 재분석하지 않습니다. 분석 장비·프로토콜이 다를 수 있으므로 직접 비교에는 임상적 해석이 필요합니다.',
                  },
                  {
                    q: 'Amylo-Cloud와 어떻게 다른가요?',
                    a: 'Amylo-Cloud는 ATN의 A(Amyloid 축적), Morph는 ATN의 N(Neurodegeneration·뇌 위축)을 평가합니다. 두 솔루션을 함께 사용하면 알츠하이머의 표준 평가 체계인 ATN 풀스택이 완성됩니다.',
                  },
                ].map(({ q, a }) => (
                  <div key={q} className="b-item" style={{ padding: '24px 28px', borderRadius: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 10, display: 'flex', gap: 12 }}>
                      <span className="num" style={{ color: 'var(--accent)', fontWeight: 800, flexShrink: 0 }}>Q.</span>
                      {q}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, paddingLeft: 28 }}>{a}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/morph')({
  component: Page,
});
